import React, { useCallback, useEffect, useState, useMemo } from 'react';

import { usePersonalisationContext } from '../../context/PersonalisationContext.jsx';
import { Label } from '../Label/index.jsx';
import { findStarMapImageUrl, starMapImageUrlToFile } from '../../utilities/starMapUtils.js';
import {
  findLocationMapImageUrl,
  locationMapImageUrlToFile,
  findLocationMapImageUrlOriginal
} from '../../utilities/locationMapUtils.js';
import { createBlankCanvasFile } from '../../utilities/doodleUtilities';

import styles from './personalisation-upload.module.css';
import { PhotoEditor } from '../PhotoEditor/index.js';
import { FileInput } from '../FileInput/index.jsx';
import { storefront } from '../../gateway/client.js';

const getMediaImageQuery = `
  query GetMediaImage($id: ID!) {
    node(id: $id) {
      ... on MediaImage {
        image {
          url
        }
      }
    }
  }
`;

/**
 * PersonalisationImageUploadSlot component for handling image uploads in personalisation steps.
 * @param {Object} props - The component props.
 * @param {import('../../gateway/personalisationData.js').Slot} props.slot - The slot configuration object containing all slot properties.
 * @param {boolean} [props.shouldShowStarMapSlot] - Whether a star map slot exists in the journey (triggers auto-open and blend mode).
 * @param {boolean} [props.shouldShowLocationMapSlot] - Whether a location map slot exists in the journey (triggers auto-open and hides zoom/flip controls).
 * @param {boolean} [props.hideMaskZoneButtons] - When true, hides zone colour picker buttons (used in GROUPED mode where colour is chosen via OptionSelection).
 * @param {string|null} [props.groupLineItemPropertyKey] - The active group name to use as the line item property key in GROUPED mode.
 * @param {((data: { selected: File, extraImages: Array, supportingImage: File|null }) => void) | null} [props.onImageSaved] - Called after the image is saved; used in GROUPED mode to propagate the upload to sibling group slots.
 * @returns {React.ReactElement} The rendered PersonalisationImageUploadSlot component.
 */
export const PersonalisationImageUploadSlot = ({
  slot,
  shouldShowStarMapSlot = false,
  shouldShowLocationMapSlot = false,
  hideMaskZoneButtons = false,
  groupLineItemPropertyKey = null,
  onImageSaved = null
}) => {
  const { updateSelections, selections, personalisationSteps, currentStepId } = usePersonalisationContext();
  const step = personalisationSteps?.get(currentStepId);

  // State for storing the mask SVG content.
  const [maskSvgBlob, setMaskSvg] = useState(/** @type {Blob | null} */ (null));
  const [backgroundImageUrl, setBackgroundImageUrl] = useState(step.baseVisualiserImage);
  const [shouldAutoOpen, setShouldAutoOpen] = useState(false);
  const [resetScaleTrigger, setResetScaleTrigger] = useState(0);

  // Get existing selection for this slot.
  const existingSelection = selections.slots?.find((selection) => selection.id === slot.id);
  // @ts-expect-error - selected can be File or other types, instanceof check is valid at runtime
  const existingFile = existingSelection?.selected instanceof File ? existingSelection.selected : null;
  const requiresMasking = slot.targetImagePlaceholder !== null;

  /**
   * Finds star map image URL from any slot in the context when shouldShowStarMapSlot is enabled.
   *
   * @returns {string | null} The star map image URL (data URL or regular URL) or null if not found.
   */
  const starMapImageUrl = useMemo(() => {
    if (!shouldShowStarMapSlot) {
      return null;
    }
    return findStarMapImageUrl(selections.slots);
  }, [shouldShowStarMapSlot, selections.slots]);

  /**
   * Converts star map image URL to File object when shouldShowStarMapSlot is enabled.
   * This File will be passed to PhotoEditor to auto-open with the star map image.
   */
  const starMapFile = useMemo(() => {
    if (!shouldShowStarMapSlot || !starMapImageUrl) {
      return null;
    }
    return starMapImageUrlToFile(starMapImageUrl, 'star-map.png');
  }, [shouldShowStarMapSlot, starMapImageUrl]);

  /**
   * Finds location map image URL from any slot in the context when shouldShowLocationMapSlot is enabled.
   *
   * @returns {string | null} The location map image URL (data URL or regular URL) or null if not found.
   */
  const locationMapImageUrl = useMemo(() => {
    if (!shouldShowLocationMapSlot) {
      return null;
    }
    return findLocationMapImageUrl(selections.slots);
  }, [shouldShowLocationMapSlot, selections.slots]);

  /**
   * Converts location map image URL to File object when shouldShowLocationMapSlot is enabled.
   * This File will be passed to PhotoEditor to auto-open with the location map image (inverted version for display).
   */
  const locationMapFile = useMemo(() => {
    if (!shouldShowLocationMapSlot || !locationMapImageUrl) {
      return null;
    }
    return locationMapImageUrlToFile(locationMapImageUrl, 'location-map.png');
  }, [shouldShowLocationMapSlot, locationMapImageUrl]);

  /**
   * Finds location map original image URL (non-inverted, for factory) from any slot in the context when shouldShowLocationMapSlot is enabled.
   *
   * @returns {string | null} The original location map image URL (data URL or regular URL) or null if not found.
   */
  const locationMapImageUrlOriginal = useMemo(() => {
    if (!shouldShowLocationMapSlot) {
      return null;
    }
    return findLocationMapImageUrlOriginal(selections.slots);
  }, [shouldShowLocationMapSlot, selections.slots]);

  /**
   * Converts location map original image URL to File object (for factory) when shouldShowLocationMapSlot is enabled.
   * This File will be sent to the factory (non-inverted version).
   */
  const locationMapFileOriginal = useMemo(() => {
    if (!shouldShowLocationMapSlot || !locationMapImageUrlOriginal) {
      return null;
    }
    return locationMapImageUrlToFile(locationMapImageUrlOriginal, 'location-map-original.png');
  }, [shouldShowLocationMapSlot, locationMapImageUrlOriginal]);

  /* Creates a blank white canvas File when doodle slot has no existing file.
   * This prepopulates the PhotoEditor with a fresh 16:9 1000px wide canvas.
   */
  const doodleFile = useMemo(() => {
    if (!slot.doodle || existingFile) {
      return null;
    }
    return createBlankCanvasFile(2000, 16 / 9);
  }, [slot.doodle, existingFile]);

  // Fetch and set the mask SVG from the target image placeholder.
  useEffect(() => {
    if (requiresMasking) {
      (async () => {
        try {
          const response = await fetch(slot.targetImagePlaceholder.url);
          if (!response.ok) {
            throw new Error(`Failed to fetch mask: ${response.statusText}`);
          }

          const imageBlob = await response.blob();
          setMaskSvg(imageBlob);
        } catch (error) {
          console.error('Failed to fetch mask:', error);
        }
      })();
    }
  }, [slot.targetImagePlaceholder]);

  useEffect(() => {
    if (slot.visualisationGroup) {
      (async () => {
        if (slot.visualisationGroup?.backgroundImage) {
          // Get the visualisation group background url from Shopify.
          const { data, errors } = await storefront.request(getMediaImageQuery, {
            variables: {
              id: slot.visualisationGroup?.backgroundImage
            }
          });
          if (errors) {
            console.error('Failed to fetch visualisation group background image:', errors);
          }
          setBackgroundImageUrl(data?.node?.image?.url);
        }
      })();
    }
  }, [slot.visualisationGroup]);

  // Whether either map type is active.
  const hasMapSlot = shouldShowStarMapSlot || shouldShowLocationMapSlot;

  useEffect(() => {
    // Check if we should auto-open:
    // - map slot with prepopulated map file (star or location), or
    // - doodle slot with blank canvas,
    // and no existing user file.
    const shouldTrigger =
      (hasMapSlot && (locationMapFile || starMapFile) && !existingFile) || (slot.doodle && doodleFile && !existingFile);

    // @todo: This is a hack to reset the scale to 1 after the editor opens as well as a delay on opening to allow filemask to load in properly. It should be removed when we have a better solution.
    if (shouldTrigger) {
      const timeoutId = setTimeout(() => {
        setShouldAutoOpen(true);
        // Trigger scale reset after editor opens.
        setResetScaleTrigger((prev) => prev + 1);
      }, 200);

      // Cleanup timeout on unmount or when dependencies change.
      return () => {
        clearTimeout(timeoutId);
        setShouldAutoOpen(false);
      };
    } else {
      // Reset auto-open state if conditions are not met.
      setShouldAutoOpen(false);
    }
  }, [hasMapSlot, locationMapFile, starMapFile, existingFile, slot.doodle, doodleFile]);
  /**
   * Handles file selection from the PhotoEditor component.
   * @param {Object} data - The file selection data containing user and edited images.
   * @param {Object} data.userImage - The original user image data.
   * @param {Object} data.editedImage - The edited image data.
   * @param {Object} [data.outlinedImage] - The edited image with mask outline (for cart properties).
   */
  const handleFileSelect = useCallback(
    (data) => {
      const { editedImage = null, outlinedImage = null } = data ?? {};
      // Handle both the old format and new format.
      const editedImageFile = editedImage?.file || null;
      const outlinedImageFile = outlinedImage?.file || null;
      // Build extraImages array - start with the uploaded image.
      const extraImagesArray = [];

      if (editedImageFile) {
        extraImagesArray.push({ data: editedImageFile, type: 'original' });
      }
      // Add star map image if available.
      if (shouldShowStarMapSlot && starMapFile) {
        extraImagesArray.push({ data: starMapFile, type: 'star-map' });
      }
      // Add location map original image (for factory) if available.
      // Use original (non-inverted) version for factory, display version is shown in photo editor.
      if (shouldShowLocationMapSlot && locationMapFileOriginal) {
        extraImagesArray.push({ data: locationMapFileOriginal, type: 'location-map' });
      }

      // Update selections with all images (uploaded + star map + location map).
      // When an outlined image exists, use it as supportingImage so the
      // mask outline appears in the cropped cart property image.
      updateSelections({
        slotId: slot.id,
        itemSelected: editedImageFile, // @TODO: This needs removing as we don't use it - it gets overridden.
        extraImages: extraImagesArray.length > 0 ? extraImagesArray[0] : null,
        supportingImage: outlinedImageFile || editedImageFile,
        groupLineItemPropertyKey
      });
      // If we have multiple images, add the rest.
      for (let i = 1; i < extraImagesArray.length; i++) {
        const currentSelection = selections.slots?.find((s) => s.id === slot.id);
        updateSelections({
          slotId: slot.id,
          itemSelected: currentSelection?.selected || editedImageFile || null,
          extraImages: extraImagesArray[i],
          groupLineItemPropertyKey
        });
      }

      // Notify parent (GROUPED mode) so the same image is propagated to sibling group slots.
      onImageSaved?.({
        selected: editedImageFile,
        extraImages: extraImagesArray,
        supportingImage: outlinedImageFile || editedImageFile || null
      });
    },
    [
      updateSelections,
      slot.id,
      shouldShowStarMapSlot,
      shouldShowLocationMapSlot,
      starMapFile,
      locationMapFileOriginal,
      selections.slots,
      groupLineItemPropertyKey,
      onImageSaved
    ]
  );

  const {
    allowedFileTypes,
    maxFileSize,
    uiHeading,
    uiSubheading,
    isRequired,
    engraving,
    fingerprint,
    doodle,
    textColour = null
  } = slot;

  const uploadType = engraving ? 'engraving' : 'photo';

  // Determine the file to use: existing > location map > star map > doodle
  const hasExistingFile = existingFile || (hasMapSlot ? locationMapFile || starMapFile : null) || doodleFile;

  if (!requiresMasking) {
    return (
      <div className={slot.disabled ? styles.disabled : ''}>
        <Label required={isRequired} htmlFor={slot.id}>
          {uiHeading}
        </Label>
        <FileInput
          onFileSelect={handleFileSelect}
          disabled={slot.disabled || false}
          buttonVariant="primary"
          buttonSize="small"
          buttonColor="orange"
          maxFileSize={maxFileSize}
          id={slot.id}
          selectedFile={existingFile}
          accept={allowedFileTypes?.join(',')}
        />
      </div>
    );
  }

  return (
    <div className={slot.disabled ? styles.disabled : ''}>
      <Label required={isRequired} htmlFor={slot.id}>
        {uiHeading}
      </Label>
      <PhotoEditor
        accept={allowedFileTypes?.join(',')}
        onFileSelect={handleFileSelect}
        disabled={slot.disabled || false}
        buttonVariant="primary"
        buttonSize="small"
        buttonColor="orange"
        maxFileSize={maxFileSize}
        id={slot.id}
        selectedFile={hasExistingFile}
        autoOpen={shouldAutoOpen ? true : undefined}
        label="Choose File"
        stepImageUrl={backgroundImageUrl}
        uploadType={uploadType}
        textColour={engraving ? textColour : null}
        maskSvgBlob={maskSvgBlob}
        showStarMapSlot={shouldShowStarMapSlot}
        showLocationMapSlot={shouldShowLocationMapSlot}
        resetScaleTrigger={hasMapSlot ? resetScaleTrigger : undefined}
        maskImageConfig={{
          url: slot.targetImagePlaceholder.url,
          width: slot.targetImagePlaceholder.width,
          height: slot.targetImagePlaceholder.height
        }}
        positioning={{
          x: Number(slot.positionX),
          y: Number(slot.positionY),
          scale: Number(slot.scale),
          rotation: Number(slot.rotation)
        }}
        maskZones={slot.imageMaskZones || []}
        hideMaskZoneButtons={hideMaskZoneButtons}
        doodle={doodle}
        overlayImageUrl={slot.overlayImage?.url || null}
      />
      {uiSubheading && <p className={'body-sm ' + styles.subheading}>{uiSubheading}</p>}
    </div>
  );
};
